import { fetchWithAuthToken, postWithAuthToken, noResponseErrorMsg } from '../../axios';
import GeoJSON from 'ol/format/GeoJSON';
import { hideLoading, showError, showLoading, showSuccess } from '../../js/ui-helpers';
// #1544
import { addFeatures } from '../../js/tools/mhp';
export const BAT = 'BAT';
export const MHP = 'MHP';
export const SST = 'SST';

const initialState = () => ({
    tools: [BAT, MHP, SST],
    currentTool: BAT,
    workingAreaIsLoaded: false,
    workingAreaFeatures: [],
    bat: {
        pointQueryResult: null,
        results: null,
    },
    mhp: {
        results: null,
    },
    sst: {
        results: null,
    }
});
const state = initialState();

const getters = {
    getCurrentTool: state => state.currentTool,
    getAllTools: state => state.tools,
    getPointQueryResult: state => state.bat.pointQueryResult,
    getWorkingAreaFeatures: state => state.workingAreaFeatures,
    isWorkingAreaLoaded: state => state.workingAreaIsLoaded,
    batIsSelected: state => state.currentTool===BAT,
    mhpIsSelected: state => state.currentTool===MHP,
    sstIsSelected: state => state.currentTool===SST,
    getBatResults: state => state.bat.results,
    getMhpResults: state => state.mhp.results,
    getSstResults: state => state.sst.results,
    showBatResults: (state,getters) =>
    {
        return getters.batIsSelected && Boolean(state.bat.results);
    },
    showMhpResults: (state,getters) =>
    {
        return getters.mhpIsSelected && Boolean(state.mhp.results);
    },
    showSstResults: (state,getters) =>
    {
        return getters.sstIsSelected && Boolean(state.sst.results);
    },
    showResults: (state, getters) =>
    {
        return (getters.showBatResults || getters.showMhpResults || getters.showSstResults);
    },
    getBatEnergyCosts: state => {
        if (!state.bat.results) return {};
        const results = state.bat.results;
        return {
            'Solar Home System': results.energy_costs.shs.cost_series,
            'Micro-Hydro Power': results.energy_costs.mhp.cost_series,
            'Grid Extension': results.energy_costs.grid.cost_series,
            'Solar Mini-Grid': results.energy_costs.solar_minigrid.cost_series,
        };
    },
    // TODO Delete isResultAvailable later
    isResultAvailable: state =>
    {
        return (
            (state.currentTool===BAT && state.bat.results) ||
            (state.currentTool===MHP && state.mhp.results) ||
            (state.currentTool===SST && state.sst.results)
        );
    },
};

const actions = {
    setCurrentTool: ({commit}, tool) =>
    {
        commit('SET_CURRENT_TOOL', tool);
    },
    async fetchWorkingArea({commit})
    {
        commit('SET_WORKING_AREA_IS_LOADED', false);
        try {
            const response = await fetchWithAuthToken("/api/working-area/");
            const features = (new GeoJSON()).readFeatures(response.data);
            commit('SET_WORKING_AREA_FEATURES', features)
            commit('SET_WORKING_AREA_IS_LOADED', true);
        }
        catch (error)
        {
            const msg = !error.response ? noResponseErrorMsg : "Couldn't load your working area";
            showError("WorkingArea", msg);
        }
    },
    async performBatPointAnalysis({commit}, point)
    {
        try
        {
            commit('SET_POINT_QUERY_RESULT', null);
            const response = await postWithAuthToken('api/bat/point-query/', { point });
            commit('SET_POINT_QUERY_RESULT', response.data);
            return {success: true};
        }
        catch (error)
        {
            const msg = (error.response) ?
                error.response.data.error
                :
                noResponseErrorMsg;
                
            showError("BAT Point Tool",msg);
            return {success: false};
        }
    }
    ,
    async performBatZonalAnalysis({commit}, polygon)
    {
        try
        {
            showLoading("Processing...");
            const response = await postWithAuthToken('api/bat/zonal-query/', { polygon });
            commit('SET_BAT_RESULTS', response.data);
            showSuccess("BAT Polygon Tool","Processing Complete! Scroll down to view results.");
            hideLoading();
            return {success: true, data: response.data };
        }
        catch (error)
        {
            const msg = (error.response) ?
                error.response.data.error
                :
                noResponseErrorMsg;
            showError("BAT Polygon Tool",msg);
            hideLoading();
            return {success: false};
        }
    },
    async performMhpAnalysis({commit}, form)
    {
        try
        {
            showLoading("Processing...");
            const response = await postWithAuthToken('api/mhp/calculate', form);
            commit('SET_MHP_RESULTS', response.data);
            addFeatures({
                powerhouse: response.data.powerhouse_geojson,
                aoi: response.data.aoi_geojson,
                intake: response.data.intake_geojson,
                upstream: response.data.upstream_geojson,
            });
            showSuccess("MHP Tool","Processing Complete! Scroll down to view results.");
            hideLoading();
            return {success: true};
        }
        catch (error)
        {
            const msg = (error.response) ?
                error.response.data.error
                :
                error;
            showError("MHP Tool", msg);
            hideLoading();
            return {success: false};
        }
    },
    async performSstAnalysis({commit}, params)
    {
        console.assert(params.sddmm, "Have to provide sddmm in performSstAnalysis");
        try
        {
            showLoading("Processing...");
            console.log(params)
            const response = await postWithAuthToken('api/sst/calculate', params);
            commit('SET_SST_RESULTS', response.data);
            showSuccess("SST Tool","Processing Complete! Scroll down to view results.");
            hideLoading();
            return {success: true, data: response.data};
        }
        catch (error)
        {
            const msg = (error.response) ?
                error.response.data.error
                :
                error;
            showError("SST Tool", msg);
            hideLoading();
            return {success: false};
        }
    },
    setSstResults({commit}, results)
    {
        commit('SET_SST_RESULTS', results);
    },
    setMhpResults({commit}, results)
    {
        commit('SET_MHP_RESULTS', results);
    },
    setBatResults({commit}, results)
    {
        commit('SET_BAT_RESULTS', results);
    },
    clearResults({commit}, {bat=false,mhp=false,sst=false,all=false}={})
    {
        if (sst===true  || all) commit('SET_SST_RESULTS', null);
        if (mhp===true || all) commit('SET_MHP_RESULTS', null);
        if (bat===true || all) commit('SET_BAT_RESULTS', null);
    }
};

const mutations = {
    SET_CURRENT_TOOL: (state, tool) =>
    {
        console.assert(state.tools.includes(tool));
        state.currentTool = tool;
    },
    SET_POINT_QUERY_RESULT: (state, value) =>
    {
        state.bat.pointQueryResult = value;
    },
    SET_BAT_RESULTS: (state, value) =>
    {
        state.bat.results = value;
    },
    SET_WORKING_AREA_FEATURES: (state, value) =>
    {
        state.workingAreaFeatures = value;
    },
    SET_WORKING_AREA_IS_LOADED: (state, isLoaded) =>
    {
        state.workingAreaIsLoaded = isLoaded;
    },
    SET_MHP_RESULTS: (state, results) =>
    {
        state.mhp.results = results;
    },
    SET_SST_RESULTS: (state, results) =>
    {
        state.sst.results = results;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
